// Lagerplatser → the view of the warehouse at the top right, beside the tree (owner 2026-11-04,
// mockup admin-v1/locations-view, answers the same day). Drawing or 3D, one at a time; the choice
// is remembered per user in this browser. What it shows follows the selection:
//   nothing selected → the whole warehouse (zones outlined, every rack shaded by how full its aisle is)
//   a zone (or any place with aisles under it) → only its racks, from above
//   an aisle → only the aisle, from the front
//   a bin → its aisle from the front with the bin marked
//   a place the layout does not draw (GOLVET, OPLACERAT) → no view.
// A click in the view selects in the tree: a zone frame or a rack in the whole warehouse, a rack in a
// zone, a bin from the front. The uploaded floor plan lies faint behind the racks. Without a
// published layout the tree's own drawings are shown (zone plan, aisle elevation) and there is no 3D.

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { BundleLocation, LayoutOut, RackElement } from "../api/client.js";
import { effectiveSettings } from "../api/client.js";
import { t } from "../i18n.js";
import { RackElevation, ZonePlan } from "../ui/map.jsx";
import { binCodes } from "../ui/bin-codes.js";
import type { RackTypeLike } from "../ui/goto-scene.js";

type Mode = "plan" | "3d";
type Scope = { kind: "all"; racks: RackElement[] } | { kind: "zone"; racks: RackElement[]; title: string } | { kind: "aisle"; racks: RackElement[]; title: string; bin: string | null };

export interface LayoutSettings { binCode: string; rackHeight: number; rackTypes: RackTypeLike[] }

const modeKey = () => `trail.admin.locView.${effectiveSettings().actorId}`;
function savedMode(): Mode { try { return localStorage.getItem(modeKey()) === "3d" ? "3d" : "plan"; } catch { return "plan"; } }

const Scene3D = lazy(() => import("./Warehouse3D.jsx"));

export function WarehouseView({ layout, settings, locations, occupiedIds, selected, onPick }: {
  layout: LayoutOut | null;
  settings: LayoutSettings;
  locations: readonly BundleLocation[];
  occupiedIds: ReadonlySet<string>;
  selected: BundleLocation | null;
  onPick: (l: BundleLocation) => void;
}) {
  const [mode, setMode] = useState<Mode>(savedMode);
  const choose = (m: Mode) => { setMode(m); try { localStorage.setItem(modeKey(), m); } catch { /* the choice lasts this visit */ } };
  const byCode = useMemo(() => new Map(locations.map((l) => [l.code, l])), [locations]);
  const byPath = useMemo(() => new Map(locations.map((l) => [l.path, l])), [locations]);
  const racks = useMemo(() => (layout?.elements ?? []).filter((e): e is RackElement => e.kind === "rack" && !!e.aisleCode), [layout]);
  const aisleLoc = (r: RackElement) => (r.aisleCode ? byCode.get(r.aisleCode) ?? null : null);
  const zoneOf = (r: RackElement): BundleLocation | null => { const a = aisleLoc(r); return a ? byPath.get(a.path.split("/")[0] ?? "") ?? null : null; };

  const scope: Scope | null = useMemo(() => {
    if (!selected) return { kind: "all", racks };
    const own = racks.filter((r) => r.aisleCode === selected.code);
    if (own.length && selected.kind !== "bin") return { kind: "aisle", racks: own, title: selected.code, bin: null };
    if (selected.kind === "bin") {
      // The nearest ancestor that a rack draws.
      const parts = selected.path.split("/");
      for (let i = parts.length - 1; i >= 1; i--) {
        const anc = byPath.get(parts.slice(0, i).join("/"));
        const rs = anc ? racks.filter((r) => r.aisleCode === anc.code) : [];
        if (anc && rs.length) return { kind: "aisle", racks: rs, title: anc.code, bin: selected.code };
      }
      return null;
    }
    const under = racks.filter((r) => { const a = aisleLoc(r); return !!a && a.path.startsWith(`${selected.path}/`); });
    return under.length ? { kind: "zone", racks: under, title: selected.code } : null;
  }, [selected, racks, byPath, byCode]);

  // Bins with something on them, by code; aisles' share of full bins for the shading.
  const occupiedCodes = useMemo(() => new Set(locations.filter((l) => occupiedIds.has(l.id)).map((l) => l.code)), [locations, occupiedIds]);
  const fullness = useMemo(() => {
    const m = new Map<string, { bins: number; used: number }>();
    for (const l of locations) {
      if (l.kind !== "bin") continue;
      const parts = l.path.split("/");
      for (let i = 1; i < parts.length; i++) {
        const p = parts.slice(0, i).join("/");
        const e = m.get(p) ?? { bins: 0, used: 0 };
        e.bins++; if (occupiedIds.has(l.id)) e.used++;
        m.set(p, e);
      }
    }
    return m;
  }, [locations, occupiedIds]);
  // The 3D scene's scope: which aisles are in view, front or from above, the marked bin. Stable between renders.
  const scope3d = useMemo(() => (scope ? { aisles: scope.kind === "all" ? null : new Set(scope.racks.map((r) => r.aisleCode ?? "")), front: scope.kind === "aisle", selected: scope.kind === "aisle" ? scope.bin : null } : null), [scope]);

  if (!layout || racks.length === 0 || !scope3d) return <TreeFallback locations={locations} occupiedIds={occupiedIds} selected={selected} onPick={onPick} />;
  if (!scope) return null;

  const title = scope.kind === "all" ? t("adm.loc.view.all") : scope.kind === "zone" ? t("adm.loc.view.zone", { code: scope.title }) : t("adm.loc.view.aisle", { code: scope.title });
  const height = scope.kind === "all" ? 420 : 340;
  const pickBin = (code: string, aisle: string) => {
    if (scope.kind === "aisle") { const b = byCode.get(code); if (b) onPick(b); return; }
    const a = byCode.get(aisle); if (a) onPick(a);
  };
  const hint = mode === "3d" ? t("adm.loc.view.hint3d") : scope.kind === "all" ? t("adm.loc.view.hintAll") : scope.kind === "zone" ? t("adm.loc.view.hintZone") : t("adm.loc.view.hintAisle");

  return (
    <div className="card wv">
      <div className="hd"><span>{title}</span>
        <span className="seg"><button type="button" className={mode === "plan" ? "on" : ""} onClick={() => choose("plan")}>{t("adm.loc.view.plan")}</button><button type="button" className={mode === "3d" ? "on" : ""} onClick={() => choose("3d")}>3D</button></span>
      </div>
      <div className="wv-view" style={{ height }}>
        {mode === "3d"
          ? <Suspense fallback={<p className="ahint" style={{ padding: 12 }}>{t("common.loading")}</p>}>
              <Scene3D racks={racks} unitPerMm={layout.plan.unitPerMm || 1} settings={settings} occupied={occupiedCodes} scope={scope3d} onPick={pickBin} />
            </Suspense>
          : <Drawing layout={layout} racks={racks} scope={scope} binCode={settings.binCode} byCode={byCode} occupiedCodes={occupiedCodes} fullness={fullness} aisleLoc={aisleLoc} zoneOf={zoneOf} onPick={onPick} />}
        <div className="wv-foot"><span>{hint}</span><Legend mode={mode} front={scope.kind === "aisle"} selected={scope.kind === "aisle" && !!scope.bin} /></div>
      </div>
    </div>
  );
}

function Legend({ mode, front, selected }: { mode: Mode; front: boolean; selected: boolean }) {
  const sw = (bg: string, border = "#9aa3ae", dashed = false) => <i style={{ background: bg, borderColor: border, borderStyle: dashed ? "dashed" : "solid" }} />;
  if (mode === "3d") return <span>{sw("#e6cf9f", "#c9a26b")}{t("adm.loc.view.goods")}{sw("rgba(127,176,220,.25)")}{t("adm.loc.view.freeBin")}{selected ? <>{sw("#4ea3e0", "#0369a1")}{t("adm.loc.view.selected")}</> : null}</span>;
  if (front) return <span>{sw("#e6cf9f", "#c9a26b")}{t("adm.loc.view.used")}{sw("#fff", "#9aa3ae", true)}{t("adm.loc.view.free")}{selected ? <>{sw("rgba(3,105,161,.16)", "#0369a1")}{t("adm.loc.view.selected")}</> : null}</span>;
  return <span>{t("adm.loc.view.shade")}{sw("rgba(28,126,214,.12)")}{t("adm.loc.view.empty")}{sw("rgba(28,126,214,.82)")}{t("adm.loc.view.full")}</span>;
}

const ease = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
type VB = [number, number, number, number];
const FLY_MS = 700, FADE_MS = 280;

/** The drawing: the plan from above for the whole warehouse and a zone, the front view for an aisle.
 *  A new scope zooms the plan there (owner 2026-11-04: a soft transition when clicking around); into
 *  an aisle the plan zooms onto its rack and then fades over to the front view, and back the same way. */
function Drawing({ layout, racks, scope, binCode, byCode, occupiedCodes, fullness, aisleLoc, zoneOf, onPick }: {
  layout: LayoutOut; racks: RackElement[]; scope: Scope; binCode: string;
  byCode: Map<string, BundleLocation>; occupiedCodes: ReadonlySet<string>;
  fullness: Map<string, { bins: number; used: number }>;
  aisleLoc: (r: RackElement) => BundleLocation | null; zoneOf: (r: RackElement) => BundleLocation | null;
  onPick: (l: BundleLocation) => void;
}) {
  const u = layout.plan.unitPerMm || 1;
  const boundsOf = (rs: RackElement[], pad: number): VB => {
    const x0 = Math.min(...rs.map((r) => r.x)) - pad, y0 = Math.min(...rs.map((r) => r.y)) - pad;
    return [x0, y0, Math.max(...rs.map((r) => r.x + r.w)) + pad - x0, Math.max(...rs.map((r) => r.y + r.h)) + pad - y0];
  };
  const targetVb = (s: Scope): VB => (s.kind === "all" ? boundsOf(racks, 2200 * u) : boundsOf(s.racks, (s.kind === "zone" ? 1600 : 1200) * u));
  const codesOf = (rs: RackElement[]) => new Set(rs.map((r) => r.aisleCode ?? ""));
  /** The racks of the aisle's zone, so that the plan behind an aisle is its zone. */
  const zoneSetOf = (s: Scope): Set<string> | null => {
    const z = s.racks[0] ? zoneOf(s.racks[0]) : null;
    return z ? codesOf(racks.filter((r) => zoneOf(r)?.id === z.id)) : codesOf(s.racks);
  };
  const [vb, setVb] = useState<VB>(() => targetVb(scope));
  const [visible, setVisible] = useState<Set<string> | null>(() => (scope.kind === "all" ? null : scope.kind === "zone" ? codesOf(scope.racks) : zoneSetOf(scope)));
  const [zones, setZones] = useState(scope.kind === "all");
  const [front, setFront] = useState<Extract<Scope, { kind: "aisle" }> | null>(scope.kind === "aisle" ? scope : null);
  const [layer, setLayer] = useState<"plan" | "front">(scope.kind === "aisle" ? "front" : "plan");
  const vbRef = useRef(vb); vbRef.current = vb;
  const layerRef = useRef(layer); layerRef.current = layer;
  const frontRef = useRef(front); frontRef.current = front;
  const scopeRef = useRef(scope); scopeRef.current = scope;
  const run = useRef(0);
  const key = `${scope.kind}|${scope.kind === "all" ? "" : scope.title}|${scope.kind === "aisle" ? scope.bin ?? "" : ""}`;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const s = scopeRef.current;
    const id = ++run.current;
    const alive = () => run.current === id;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, reduce ? 0 : ms));
    const fly = (to: VB) => new Promise<void>((res) => {
      const from = vbRef.current;
      if (reduce) { setVb(to); res(); return; }
      const t0 = performance.now();
      const step = (now: number) => {
        if (!alive()) { res(); return; }
        const k = Math.min(1, (now - t0) / FLY_MS), e = ease(k);
        setVb(from.map((v, i) => v + (to[i]! - v) * e) as VB);
        if (k < 1) requestAnimationFrame(step); else res();
      };
      requestAnimationFrame(step);
    });
    void (async () => {
      // Another bin in the aisle already shown: only the mark moves.
      if (s.kind === "aisle" && layerRef.current === "front" && frontRef.current?.title === s.title) { setFront(s); return; }
      if (layerRef.current === "front") { setLayer("plan"); await sleep(FADE_MS); if (!alive()) return; }
      if (s.kind === "aisle") {
        setZones(false);
        setVisible(zoneSetOf(s));
        await fly(targetVb(s));
        if (!alive()) return;
        setFront(s);
        setLayer("front");
      } else {
        setZones(s.kind === "all");
        setVisible(s.kind === "zone" ? codesOf(s.racks) : null);
        await fly(targetVb(s));
      }
    })();
  }, [key]);
  const fade = (on: boolean): React.CSSProperties => ({ opacity: on ? 1 : 0, transition: `opacity ${FADE_MS}ms ease`, pointerEvents: on ? "auto" : "none" });
  return (
    <>
      <div className="wv-pad" style={fade(layer === "plan")}><PlanView layout={layout} racks={racks} vb={vb} visible={visible} zones={zones} fullness={fullness} aisleLoc={aisleLoc} zoneOf={zoneOf} onPick={onPick} /></div>
      {front && <div className="wv-pad" style={fade(layer === "front")}><FrontView racks={front.racks} binCode={binCode} byCode={byCode} occupiedCodes={occupiedCodes} selected={front.bin} onPick={onPick} /></div>}
    </>
  );
}

/** The plan from above: the uploaded floor plan faint behind, the racks shaded by how full their aisle is.
 *  `vb` is the part shown (animated by Drawing); racks outside `visible` fade out; `zones` outlines the zones. */
function PlanView({ layout, racks, vb, visible, zones, fullness, aisleLoc, zoneOf, onPick }: {
  layout: LayoutOut; racks: RackElement[]; vb: VB; visible: ReadonlySet<string> | null; zones: boolean;
  fullness: Map<string, { bins: number; used: number }>;
  aisleLoc: (r: RackElement) => BundleLocation | null; zoneOf: (r: RackElement) => BundleLocation | null;
  onPick: (l: BundleLocation) => void;
}) {
  const u = layout.plan.unitPerMm || 1;
  const bounds = (rs: RackElement[], pad: number): VB => {
    const x0 = Math.min(...rs.map((r) => r.x)) - pad, y0 = Math.min(...rs.map((r) => r.y)) - pad;
    return [x0, y0, Math.max(...rs.map((r) => r.x + r.w)) + pad - x0, Math.max(...rs.map((r) => r.y + r.h)) + pad - y0];
  };
  // Sizes follow the part shown, so text keeps its size on screen while zooming.
  const L = Math.max(vb[2], vb[3]);
  const fs = L / 48;
  const bg = useMemo(() => (layout.planSvg ? `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(layout.planSvg)))}` : null), [layout.planSvg]);
  const pv = layout.plan.viewBox;
  const floor = useMemo(() => bounds(racks, 4000 * u), [racks, u]);
  const zoneGroups = useMemo(() => {
    const m = new Map<string, { zone: BundleLocation; racks: RackElement[] }>();
    for (const r of racks) { const z = zoneOf(r); if (!z) continue; const e = m.get(z.id) ?? { zone: z, racks: [] }; e.racks.push(r); m.set(z.id, e); }
    return [...m.values()].sort((a, b) => a.zone.code.localeCompare(b.zone.code, "sv", { numeric: true }));
  }, [racks, zoneOf]);
  const fade = (on: boolean): React.CSSProperties => ({ opacity: on ? 1 : 0, transition: "opacity 450ms ease", pointerEvents: on ? "auto" : "none" });
  return (
    <svg viewBox={vb.join(" ")} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%", display: "block" }}>
      {bg && pv ? <image href={bg} x={pv[0]} y={pv[1]} width={pv[2]} height={pv[3]} opacity={0.25} preserveAspectRatio="none" /> : <rect x={floor[0]} y={floor[1]} width={floor[2]} height={floor[3]} fill="#f6f8fa" />}
      <g style={fade(zones)}>{layout.elements.filter((e) => e.kind === "area").map((a) => <rect key={a.id} x={a.x} y={a.y} width={a.w} height={a.h} fill="rgba(255,200,87,0.2)" stroke="#c9a227" strokeWidth={L / 900} strokeDasharray={`${L / 120} ${L / 240}`} />)}</g>
      {zoneGroups.map(({ zone, racks: rs }) => {
        const b = bounds(rs, 700 * u);
        const f = fullness.get(zone.path) ?? { bins: 0, used: 0 };
        return (
          <g key={zone.id} onClick={() => onPick(zone)} style={{ cursor: "pointer", ...fade(zones) }}>
            <rect x={b[0]} y={b[1]} width={b[2]} height={b[3]} rx={L / 300} fill="rgba(3,105,161,0.04)" stroke="#0369a1" strokeWidth={L / 700} strokeDasharray={`${L / 140} ${L / 280}`} />
            <text x={b[0] + L / 220} y={b[1] - L / 160} fontSize={L / 48} fontWeight={700} fill="#0369a1" fontFamily="Arial, sans-serif">{zone.code}</text>
            <text x={b[0] + L / 220 + (zone.code.length * L) / 60 + L / 120} y={b[1] - L / 160} fontSize={L / 80} fill="#4b5563" fontFamily="Arial, sans-serif">{t("adm.loc.view.zoneCount", { bins: f.bins, used: f.used })}</text>
          </g>
        );
      })}
      {racks.map((r) => {
        const a = aisleLoc(r);
        const f = a ? fullness.get(a.path) ?? { bins: 0, used: 0 } : { bins: 0, used: 0 };
        const share = f.bins ? f.used / f.bins : 0;
        const cx = r.x + r.w / 2, cy = r.y + r.h / 2;
        const thin = Math.min(r.w, r.h);
        // A label only where it fits across the rack at the current zoom.
        const size = Math.min(fs, thin * 0.8);
        return (
          <g key={r.id} onClick={(e) => { e.stopPropagation(); if (a) onPick(a); }} style={{ cursor: a ? "pointer" : "default", ...fade(!visible || visible.has(r.aisleCode ?? "")) }}>
            <title>{t("adm.loc.view.rackTip", { code: r.aisleCode ?? "", used: f.used, bins: f.bins })}</title>
            <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={`rgba(28,126,214,${(0.12 + share * 0.7).toFixed(2)})`} stroke="#5b6878" strokeWidth={L / 1100} />
            {size > fs * 0.45 && <text x={cx} y={cy} fontSize={size} textAnchor="middle" dominantBaseline="central" fill="#16202b" fontWeight={700} fontFamily="Arial, sans-serif" transform={r.h > r.w ? `rotate(-90 ${cx} ${cy})` : undefined}>{r.aisleCode}</text>}
          </g>
        );
      })}
    </svg>
  );
}

/** An aisle as one stands in front of it: bays left to right, levels from the floor, every compartment a cell with its code. */
function FrontView({ racks, binCode, byCode, occupiedCodes, selected, onPick }: { racks: RackElement[]; binCode: string; byCode: Map<string, BundleLocation>; occupiedCodes: ReadonlySet<string>; selected: string | null; onPick: (l: BundleLocation) => void }) {
  const bays: { b: number; levels: number; per: number; codes: string[] }[] = [];
  for (const r of [...racks].sort((a, b) => (a.bayStart ?? 1) - (b.bayStart ?? 1))) {
    const all = binCodes(r, binCode);
    let k = 0;
    for (let i = 0; i < r.bays; i++) {
      const b = (r.bayStart ?? 1) + i, o = r.overrides[String(b)] ?? {};
      const levels = o.levels ?? r.levels, per = o.compartmentsPerBay ?? r.compartmentsPerBay;
      bays.push({ b, levels, per, codes: all.slice(k, k + levels * per) });
      k += levels * per;
    }
  }
  if (!bays.length) return null;
  const maxLv = Math.max(...bays.map((b) => b.levels));
  const perMax = Math.max(...bays.map((b) => b.per));
  const cellW = 74, cellH = perMax > 2 ? 30 : 46, post = 6, left = 30, bottom = 30, pitch = cellH + 14;
  const bayW = (b: { per: number }) => b.per * cellW + 10;
  const W = left + bays.reduce((a, b) => a + bayW(b) + post, post) + 10;
  const H = 26 + maxLv * pitch + bottom;
  const floorY = H - bottom;
  let x = left;
  const parts: JSX.Element[] = [];
  for (const b of bays) {
    const top = floorY - b.levels * pitch - 8;
    parts.push(<rect key={`p${b.b}`} x={x} y={top} width={post} height={floorY - top} fill="#2f6fb0" />);
    const x0 = x + post;
    for (let lv = 1; lv <= b.levels; lv++) {
      const yb = floorY - (lv - 1) * pitch;
      if (lv > 1) parts.push(<rect key={`b${b.b}-${lv}`} x={x0} y={yb - 6} width={bayW(b)} height={6} fill="#e39b2d" />);
      for (let c = 1; c <= b.per; c++) {
        const code = b.codes[(lv - 1) * b.per + (c - 1)] ?? "";
        const loc = byCode.get(code);
        const full = occupiedCodes.has(code), sel = code === selected;
        const cx = x0 + 5 + (c - 1) * cellW, cy = yb - (lv > 1 ? 6 : 0) - cellH - 4;
        const ch = cellH - (full ? 5 : 0);
        parts.push(
          <g key={code || `${b.b}-${lv}-${c}`} onClick={loc ? () => onPick(loc) : undefined} style={{ cursor: loc ? "pointer" : "default" }} opacity={loc ? 1 : 0.4}>
            <title>{loc ? `${code} · ${full ? t("adm.loc.view.used") : t("adm.loc.view.free")}` : t("adm.loc.view.notInTree", { code })}</title>
            {full && <rect x={cx + 2} y={cy + cellH - 5} width={cellW - 8} height={5} fill="#c9a26b" />}
            <rect x={cx + 2} y={cy} width={cellW - 8} height={ch} rx={3} fill={sel ? "rgba(3,105,161,0.16)" : full ? "#e6cf9f" : "#ffffff"} stroke={sel ? "#0369a1" : "#9aa3ae"} strokeWidth={sel ? 3 : 1} strokeDasharray={full || sel ? undefined : "3 3"} />
            <text x={cx + cellW / 2 - 2} y={cy + ch / 2} fontSize={11.5} textAnchor="middle" dominantBaseline="central" fill={sel ? "#0369a1" : "#16202b"} fontWeight={sel ? 700 : 400}>{`${b.b}-${lv}-${c}`}</text>
          </g>,
        );
      }
    }
    parts.push(<text key={`t${b.b}`} x={x0 + bayW(b) / 2} y={floorY + 18} fontSize={12.5} fontWeight={700} fill="#4b5563" textAnchor="middle">{t("adm.loc.view.bay", { n: b.b })}</text>);
    x = x0 + bayW(b);
  }
  const last = bays[bays.length - 1]!;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%", display: "block" }} fontFamily="Arial, sans-serif">
      <rect x={0} y={floorY} width={W} height={3} fill="#c3c9d1" />
      {Array.from({ length: maxLv }, (_, i) => <text key={`l${i}`} x={left - 12} y={floorY - (i + 0.5) * pitch} fontSize={12} fill="#4b5563" textAnchor="middle" dominantBaseline="central">{i + 1}</text>)}
      {parts}
      <rect x={x} y={floorY - last.levels * pitch - 8} width={post} height={last.levels * pitch + 8} fill="#2f6fb0" />
    </svg>
  );
}

/** Without a published layout: the tree's own drawings, as the page had them before. */
function TreeFallback({ locations, occupiedIds, selected, onPick }: { locations: readonly BundleLocation[]; occupiedIds: ReadonlySet<string>; selected: BundleLocation | null; onPick: (l: BundleLocation) => void }) {
  const parentPath = (p: string) => (p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "");
  const byPath = new Map(locations.map((l) => [l.path, l]));
  const hasBins = (l: BundleLocation) => locations.some((b) => b.kind === "bin" && parentPath(b.path) === l.path);
  const aisle = !selected ? null : selected.kind === "bin" ? byPath.get(parentPath(selected.path)) ?? null : hasBins(selected) ? selected : null;
  const zonePath = (selected ? selected.path : locations.find((l) => !l.path.includes("/") && locations.some((b) => b.kind === "bin" && b.path.startsWith(`${l.path}/`)))?.path ?? "").split("/")[0] ?? "";
  if (!aisle && !locations.some((b) => b.kind === "bin" && b.path.startsWith(`${zonePath}/`))) return null;
  return (
    <div className="card">
      <div className="hd">{aisle ? t("adm.loc.view.aisle", { code: aisle.code }) : t("adm.loc.view.zone", { code: zonePath })} <span className="sub">{t("adm.loc.view.noLayout")}</span></div>
      <div className="bd" style={{ overflow: "auto" }}>
        {aisle
          ? <RackElevation locations={locations} aislePath={aisle.path} occupied={occupiedIds} selectedId={selected?.kind === "bin" ? selected.id : null} onPick={onPick} />
          : <ZonePlan locations={locations} zonePath={zonePath} occupied={occupiedIds} selectedId={selected?.id ?? null} onPick={onPick} />}
      </div>
    </div>
  );
}

export function useLayoutSettings(siteConfig: unknown): LayoutSettings {
  return useMemo(() => {
    const cfg = (siteConfig as { layout?: { binCode?: string; rackHeight?: number; rackTypes?: { id: string; holds: "pallet" | "carton"; height: number; levelHeights?: number[] | null }[] } } | null)?.layout;
    const rackTypes: RackTypeLike[] = (cfg?.rackTypes ?? []).map((x) => ({ id: x.id, holds: x.holds, height: x.height, levelHeights: x.levelHeights ?? null }));
    const builtIn: RackTypeLike[] = [
      { id: "pallet-eur", holds: "pallet", height: cfg?.rackHeight ?? 5000, levelHeights: null },
      { id: "shelf", holds: "carton", height: 2000, levelHeights: null },
    ];
    return { binCode: cfg?.binCode ?? "{aisle}-{bay}-{level}-{compartment}", rackHeight: cfg?.rackHeight ?? 5000, rackTypes: [...rackTypes, ...builtIn.filter((b) => !rackTypes.some((x) => x.id === b.id))] };
  }, [siteConfig]);
}
